
import { Options, Vue } from 'vue-class-component';
import NavigationService from "@/services/NavigationService";
import CourseService from "@/services/CourseService";
import { Region as RegionModel } from "@/data/region";
import { RecordIdentifier } from "@/data/global";
import { CourseEnrollmentIntentResult } from "@/data/course";
import { EnrollmentCriteriaError } from "@/data/enrollments";
import LoginForm from "@/components/LoginForm.vue";
import Alert from "@/components/Alert.vue";
import EventBusService from "@/services/EventBusService";
import LoginModal from "@/components/modals/LoginModal.vue";
import CourseOrderable from "@/components/CourseOrderable.vue";
import { parseEnrollmentErrorResult } from "@/utils/enrollment";
//import AuthService from "@/services/AuthService";
//import UrlService from "@/services/UrlService";

@Options({
    props: {
        courseId: {
            type: [Number, String],
            required: true,
        },
        regionId: {
            type: [Number, String],
            required: true,
        }
    },
    components: {
        CourseOrderable,
        LoginModal,
        Alert,
        LoginForm,
    },
    emits: [
        'enrollment',
        'withdrawal',
        'login'
    ],
})
export default class CourseEnrollment extends Vue {
    courseId!: RecordIdentifier
    regionId!: RecordIdentifier

    enrollmentRequested = false

    loginModalShown = false
    loginOpen = false

    error = false

    loading = true
    loaded = false

    loadingEnroll = false
    loadingWithdraw = false
    loadingJoinWaitlist = false
    loadingLeaveWaitlist = false

    result: CourseEnrollmentIntentResult|null = null

    get isAuthenticated() {
        return this.result?.authenticated;
    }

    get isEnrolled() {
        return !!this.result?.enrollmentStatus.course;
    }
    get isWaiting() {
        return !!this.result?.enrollmentStatus.courseWaiting;
    }

    get canEnroll() {
        return !!this.result?.enrollmentIntents?.course?.canEnroll;
    }
    get canJoinWaitlist() {
        return !!this.result?.enrollmentIntents?.course?.canJoinWaitlist;
    }

    get courseIsEnrollableDuringRegistration() {
        return this.result?.enrollmentIntents?.course?.creditRequired === false;
    }
    get registerWithCoursePrefilledUrl() {
        return NavigationService.buildRouteUrl('register', { course: this.courseId });
    }
    get registerWithRegionPrefilledUrl() {
        return NavigationService.buildRouteUrl('register', { region: this.regionId });
    }

    get isClosedCourseAndUserOnlyHasInsufficientCredit() {
        // closed course <=> credit required (customer always has to pay
        return this.result?.enrollmentIntents?.course?.creditRequired && this.hasOnlyErrorKey('UserHasCreditForCourse');
    }

    getHighestPriorityErrorsOfTag(tag?: string): EnrollmentCriteriaError[] {
        return this.result?.enrollmentIntents?.course?.errors
            .filter(error => !tag || error.tags.includes(tag))
            .reduce<EnrollmentCriteriaError[]>((list, error) => {
                if( list.length < 1 ) {
                    return [error];
                }
                const max = list[0].priority; // list will only contain entries of the same priority at all times, so use first entry
                if( error.priority > max ) {
                    return [error]; // new max priority found
                }
                if( error.priority === max ) {
                    return [...list, error]; // same max priority, so append
                }
                return list;
            }, []) || [];
    }

    get highestPriorityErrors(): EnrollmentCriteriaError[] {
        return this.getHighestPriorityErrorsOfTag();
    }

    get errorMissingCourseEnrollment() { // @TODO: remove, makes no sense in course enrollment context
        return this.highestPriorityErrors.some(error => error.key === 'UserIsEnrolledInAppointmentCourse');
    }

    onEnrollmentRequest() {
        this.loadEnrollmentIntent();
        this.enrollmentRequested = true;
    }

    onLogin() {
        this.loadEnrollmentIntent();
        this.$emit('login');
    }

    get courseNotEnrollable() {
        return this.result?.enrollmentIntents?.course?.hasError.course || false;
    }

    hasErrorKey(errorKey: string) {
        return this.result?.enrollmentIntents?.course?.errors.some(error => error.key === errorKey) || false;
    }

    hasOnlyErrorKey(errorKey: string) {
        if( !this.result?.enrollmentIntents?.course?.errors || this.result?.enrollmentIntents?.course?.errors?.length === 0 ) {
            return false;
        }

        return this.result?.enrollmentIntents?.course?.errors.every(error => error.key === errorKey) || false;
    }

    /*get isEnrolled() {
        return this.enrollment !== null;
    }*/

    onPerformEnrollment(reportAlreadyEnrolledAsSuccess = false) {
        this.loadingEnroll = true;
        return CourseService.enrollCourse(this.courseId, reportAlreadyEnrolledAsSuccess).then(result => {
            EventBusService.$notification({
                type: 'success',
                message: 'Deine Anmeldung zum Kurs war erfolgreich.',
            });

            this.$emit('enrollment');
            EventBusService.$emit('user.courses.enrollment:changed');
        }).catch(error => {
            const errorMessage = parseEnrollmentErrorResult(error.response.data?.data?.enrollmentIntents);
            EventBusService.$notification({
                type: 'error',
                message: 'Bei deiner Kursanmeldung ist ein Fehler aufgetreten' + (errorMessage ? (': ' + errorMessage) : '.'),
            });
            console.log(error.response?.data);
            //this.result = null;
            if( error.response.status === 401 ) {
                // this.loggedIn = false;
                return;
            }
            //this.error = true;
        }).finally(() => this.loadingEnroll = false);
    }

    onPerformWithdrawal() {
        this.loadingWithdraw = true;
        return CourseService.withdrawCourse(this.courseId).then(result => {
            // this.loggedIn = true;

            EventBusService.$notification({
                type: 'success',
                message: 'Deine Abmeldung vom Kurs war erfolgreich.',
            });

            this.$emit('withdrawal');
            EventBusService.$emit('user.courses.enrollment:changed');
            //this.result = result.data?.data || null;

            //this.error = false;
            //this.loaded = true;
        }).catch(error => {
            EventBusService.$notification({
                type: 'error',
                message: error.response.data?.error_temp || 'Bei deiner Kursabmeldung ist ein Fehler aufgetreten.',
            });
            console.log(error.response?.data);
            //this.result = null;
            if( error.response.status === 401 ) {
                // this.loggedIn = false;
                return;
            }
            //this.error = true;
        }).finally(() => this.loadingWithdraw = false);
    }

    onPerformJoinWaitlist() {
        this.loadingJoinWaitlist = true;
        return CourseService.joinCourseWaitlist(this.courseId).then(result => {
            // this.loggedIn = true;

            EventBusService.$notification({
                type: 'success',
                message: 'Du bist der Warteliste des Kurses erfolgreich beigetreten.',
            });

            this.$emit('enrollment'); // @TODO: decide if dedicated event should be used here?
            EventBusService.$emit('user.courses.enrollment:changed');
            //this.result = result.data?.data || null;

            //this.error = false;
            //this.loaded = true;
        }).catch(error => {
            EventBusService.$notification({
                type: 'error',
                message: 'Beim Beitreten zur Warteliste des Kurses ist ein Fehler aufgetreten.',
            });
            console.log(error.response?.data);
            //this.result = null;
            if( error.response.status === 401 ) {
                // this.loggedIn = false;
                return;
            }
            //this.error = true;
        }).finally(() => this.loadingJoinWaitlist = false);
    }

    onPerformLeaveWaitlist() {
        this.loadingLeaveWaitlist = true;
        return CourseService.leaveCourseWaitlist(this.courseId).then(result => {
            // this.loggedIn = true;

            EventBusService.$notification({
                type: 'success',
                message: 'Du hast die Warteliste des Kurses erfolgreich verlassen.',
            });

            this.$emit('withdrawal'); // @TODO: decide if dedicated event should be used here?
            EventBusService.$emit('user.courses.enrollment:changed');
            //this.result = result.data?.data || null;

            //this.error = false;
            //this.loaded = true;
        }).catch(error => {
            EventBusService.$notification({
                type: 'error',
                message: 'Beim Verlassen der Warteliste des Kurses ist ein Fehler aufgetreten.',
            });
            console.log(error.response?.data);
            //this.result = null;
            if( error.response.status === 401 ) {
                // this.loggedIn = false;
                return;
            }
            //this.error = true;
        }).finally(() => this.loadingLeaveWaitlist = false);
    }

    onOrderSuccess() {
        console.log('refresh enrollment status');
        this.loadEnrollmentIntent().then(() => {
            console.log('perform enrollment');
            this.onPerformEnrollment(true);
        });
    }

    loadEnrollmentIntent() {
        this.loading = true;
        return CourseService.getCourseEnrollmentIntent(this.courseId, true).then(result => {
            // this.loggedIn = true;

            this.result = result.data?.data || null;

            this.error = false;
            this.loaded = true;
        }).catch(error => {
            this.result = null;
            if( error.response.status === 401 ) {
                // this.loggedIn = false;
                return;
            }
            this.error = true;
        }).finally(() => this.loading = false);
    }

    mounted() {
        // this.loadEnrollmentIntent();
        this.onEnrollmentRequest();

        //EventBusService.$on('user.login', this.loadEnrollmentIntent);
        EventBusService.$on('user.courses.enrollment:changed', this.loadEnrollmentIntent);
    }
    beforeUnmount() {
        EventBusService.$off('user.courses.enrollment:changed', this.loadEnrollmentIntent);
    }
}


